import React, { useState, useEffect } from 'react';

// 歯車アイコン
const SettingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.72l-.15.1a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.38a2 2 0 0 0-.73-2.73l-.15-.1a2 2 0 0 1-1-1.72v-.51a2 2 0 0 1 1-1.72l.15-.1a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const styles = {
  container: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '24px 20px',
    border: '2px solid #eab308',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
    textAlign: 'center',
    boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
    position: 'relative',
  },
  question: { fontWeight: 'bold', marginBottom: '12px', color: '#854d0e', fontSize: '16px' },
  subText: { fontSize: '11px', color: '#9ca3af', marginBottom: '14px' },
  buttonGrid: { display: 'grid', gridTemplateColumns: '1fr', gap: '10px' },
  button: {
    padding: '12px', borderRadius: '8px', border: '1px solid #eab308',
    backgroundColor: '#fff', color: '#854d0e', cursor: 'pointer', fontWeight: 'bold'
  },
  input: { padding: '8px', borderRadius: '8px', border: '1px solid #ccc', fontSize: '16px', textAlign: 'center' },
  backBtn: { marginTop: '12px', fontSize: '12px', color: '#9ca3af', textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none' },
  settingsBtn: {
    position: 'absolute', top: '12px', right: '12px',
    background: 'none', border: 'none', color: '#ca8a04',
    cursor: 'pointer', padding: '4px', display: 'flex',
    alignItems: 'center', justifyContent: 'center'
  }
};

// 時間帯グループ → 絞り込み → 推定時刻（中央値）
const HOUR_MAP = {
  morning: { early: 6,  late: 9  },  // 卯(5-7) / 辰(7-9)
  midday:  { early: 12, late: 15 },  // 午(11-13) / 未(13-15)
  evening: { early: 18, late: 21 },  // 酉(17-19) / 戌(19-21)
  night:   { early: 0,  late: 3  },  // 子(23-1) / 丑(1-3)
};

export default function FortuneDiagnosis({ fortune, currentBirthDate, onSave, t, isJapan, isLoading }) {
  const [step, setStep] = useState(currentBirthDate ? 'INIT' : 'DATE');
  const [tempDate, setTempDate] = useState(currentBirthDate || '');
  const [answers, setAnswers] = useState({ timeGroup: null, refine: null });
  const [loading, setLoading] = useState(false);

  const TXT = {
    next:     isJapan ? '次へ（詳細診断）' : 'Next (Diagnosis)',
    back:     isJapan ? '戻る' : 'Back',
    settings: isJapan ? '設定・再診断' : 'Settings',

    // Q1
    q1:     isJapan ? 'Q1. 一番調子のよい時間帯は？' : 'Q1. When are you most energetic?',
    q1_sub: isJapan ? '自然なリズムで、最も活動的・集中できる時間帯を選んでください' : 'Choose your natural peak energy period',
    q1_options: [
      {
        key: 'morning',
        main: isJapan ? '🌅 早朝〜午前' : '🌅 Early Morning',
        sub:  isJapan ? '（5〜11時ごろ）' : '(5am – 11am)',
      },
      {
        key: 'midday',
        main: isJapan ? '☀️ 昼〜午後' : '☀️ Midday – Afternoon',
        sub:  isJapan ? '（11〜17時ごろ）' : '(11am – 5pm)',
      },
      {
        key: 'evening',
        main: isJapan ? '🌆 夕方〜夜' : '🌆 Evening – Night',
        sub:  isJapan ? '（17〜23時ごろ）' : '(5pm – 11pm)',
      },
      {
        key: 'night',
        main: isJapan ? '🌙 深夜〜明け方' : '🌙 Late Night – Dawn',
        sub:  isJapan ? '（23〜5時ごろ）' : '(11pm – 5am)',
      },
    ],

    // Q2 絞り込み（時間帯別）
    q2: isJapan ? 'Q2. より具体的には？' : 'Q2. More specifically?',
    q2_options: {
      morning: [
        {
          key: 'early',
          main: isJapan ? '夜明けとともに目が覚める' : 'Wake naturally at dawn',
          sub:  isJapan ? '（5〜8時台が最もすっきり）' : '(Most alert at 5–8am)',
        },
        {
          key: 'late',
          main: isJapan ? '朝食後から本調子になる' : 'Peak after breakfast',
          sub:  isJapan ? '（8〜11時台が最も活動的）' : '(Most active at 8–11am)',
        },
      ],
      midday: [
        {
          key: 'early',
          main: isJapan ? '昼前後が一番集中できる' : 'Best focus around noon',
          sub:  isJapan ? '（11〜14時台）' : '(11am–2pm)',
        },
        {
          key: 'late',
          main: isJapan ? '午後2〜4時台が最もノッてくる' : 'Hit stride in mid-afternoon',
          sub:  isJapan ? '（14〜17時台）' : '(2pm–5pm)',
        },
      ],
      evening: [
        {
          key: 'early',
          main: isJapan ? '夕方5〜7時ごろ調子が上がる' : 'Energy rises at dusk',
          sub:  isJapan ? '（17〜20時台）' : '(5pm–8pm)',
        },
        {
          key: 'late',
          main: isJapan ? '夜9〜11時が最も活発' : 'Most active late evening',
          sub:  isJapan ? '（20〜23時台）' : '(8pm–11pm)',
        },
      ],
      night: [
        {
          key: 'early',
          main: isJapan ? '深夜0〜2時が最も集中できる' : 'Peak focus around midnight',
          sub:  isJapan ? '（23〜2時台）' : '(11pm–2am)',
        },
        {
          key: 'late',
          main: isJapan ? '明け方3〜5時ごろまで動ける' : 'Active until pre-dawn',
          sub:  isJapan ? '（2〜5時台）' : '(2am–5am)',
        },
      ],
    },
  };

  useEffect(() => {
    if (currentBirthDate && step === 'DATE' && !answers.timeGroup) {
      setStep('INIT');
      setTempDate(currentBirthDate);
    }
  }, [currentBirthDate]);

  const handleTimeGroup = (value) => {
    setAnswers({ timeGroup: value, refine: null });
    setStep('Q_REFINE');
  };

  const handleRefine = async (value) => {
    const finalAnswers = { ...answers, refine: value };
    setAnswers(finalAnswers);
    setStep('LOADING'); // 即座に待機画面へ
    const estimatedHour = HOUR_MAP[finalAnswers.timeGroup]?.[value] ?? 12;
    await onSave({ birthDate: tempDate, estimatedHour, diagnosisLog: finalAnswers });
    setStep('INIT');
  };

  // 1. 診断済み
  if (step === 'INIT' && currentBirthDate) {
    return (
      <div style={styles.container}>
        <button onClick={() => setStep('DATE')} style={styles.settingsBtn} title={TXT.settings}>
          <SettingsIcon />
        </button>
        <div style={styles.question}>🔮 {t('fortune_title')}</div>
        <div style={{ fontSize: '15px', fontWeight: '500', lineHeight: '1.6', whiteSpace: 'pre-wrap', minHeight: '60px', color: isLoading ? '#9ca3af' : 'inherit' }}>
          {isLoading ? (isJapan ? '🔄 運勢を読み込み中...' : '🔄 Loading fortune...') : (fortune ? fortune : t('fortune_placeholder'))}
        </div>
      </div>
    );
  }

  // 2. 生年月日入力
  if (step === 'DATE') {
    return (
      <div style={styles.container}>
        <div style={styles.question}>{t('ask_birthdate')}</div>
        <input type="date" value={tempDate} onChange={(e) => setTempDate(e.target.value)} style={styles.input} />
        <div style={{ marginTop: '16px' }}>
          <button onClick={() => { if (!tempDate) return; setStep('Q_TIME'); }} style={styles.button} disabled={!tempDate}>
            {TXT.next}
          </button>
        </div>
        {currentBirthDate && (
          <button onClick={() => setStep('INIT')} style={styles.backBtn}>{t('cancel')}</button>
        )}
      </div>
    );
  }

  // 3. Q1：4分割の時間帯選択
  if (step === 'Q_TIME') {
    return (
      <div style={styles.container}>
        <div style={styles.question}>{TXT.q1}</div>
        <div style={styles.subText}>{TXT.q1_sub}</div>
        <div style={styles.buttonGrid}>
          {TXT.q1_options.map(({ key, main, sub }) => (
            <button key={key} style={styles.button} onClick={() => handleTimeGroup(key)}>
              {main}
              <div style={{ fontSize: '10px', fontWeight: 'normal' }}>{sub}</div>
            </button>
          ))}
        </div>
        <button onClick={() => setStep('DATE')} style={styles.backBtn}>{TXT.back}</button>
      </div>
    );
  }

  // 4. 待機画面
  if (step === 'LOADING') {
    return (
      <div style={styles.container}>
        <div style={{ fontSize: '40px', marginBottom: '16px' }}>🔮</div>
        <div style={{ fontWeight: 'bold', color: '#854d0e', fontSize: '16px', marginBottom: '8px' }}>
          {isJapan ? '占い中...' : 'Reading your fortune...'}
        </div>
        <div style={{ fontSize: '13px', color: '#9ca3af' }}>
          {isJapan ? '少々お待ちください' : 'Please wait a moment'}
        </div>
      </div>
    );
  }

  // 5. Q2：絞り込み
  if (step === 'Q_REFINE') {
    const opts = TXT.q2_options[answers.timeGroup] || [];
    return (
      <div style={styles.container}>
        <div style={styles.question}>{TXT.q2}</div>
        <div style={styles.buttonGrid}>
          {opts.map(({ key, main, sub }) => (
            <button key={key} style={styles.button} onClick={() => handleRefine(key)}>
              {main}
              <div style={{ fontSize: '10px', fontWeight: 'normal' }}>{sub}</div>
            </button>
          ))}
        </div>
        <button onClick={() => setStep('Q_TIME')} style={styles.backBtn}>{TXT.back}</button>
      </div>
    );
  }

  return null;
}
